import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaChevronLeft, FaDownload, FaFileDownload } from "react-icons/fa";
import { FiMenu } from "react-icons/fi";
import {
  HiArrowLeft,
  HiArrowRight,
  HiClipboardCopy,
  HiLockClosed,
  HiOutlineChevronDown,
  HiOutlineLockClosed,
  HiX,
} from "react-icons/hi";
import { Link, useNavigate } from "react-router-dom";

import CodeViewer from "../../components/CodeViewer";
import CustomVideoPlayer from "../../components/CustomVideoPlayer";
import MatchingQuestion from "../../components/MatchingQuestion";
import { useAuth } from "../../context/AuthContext";
import { finishExamOnLogout } from "../../context/examService";
import { isCodeLTR } from "../../utils/hasCodeChars";
import FinalAssessment from "./FinalAssessment";

// Type constants
const TYPE_PRE_ASSESSMENT = 1;
const TYPE_VIDEO = 2;
const TYPE_IN_ASSESSMENT = 3;
const TYPE_POST_ASSESSMENT = 4;
const TYPE_DOCUMENT = 5;
const TYPE_SUMMARY = 6;
const TYPE_FINAL_ASSESSMENT = 10;

function normalizeMatchingQuestion(q) {
  const left = [];
  const right = [];

  q?.options?.forEach((opt, index) => {
    // LEFT SIDE
    if (opt.option1) {
      left.push({
        id: `left-${opt.id}`,
        optionId: opt.id,
        type: "text",
        value: opt.option1,
        answerId: opt.answer?.id,
      });
    }

    if (opt.image1) {
      left.push({
        id: `left-img-${opt.id}`,
        optionId: opt.id,
        type: "image",
        value: opt.image1,
        answerId: opt.answer?.id,
      });
    }

    // RIGHT SIDE (NO UNIQUE FILTER)
    if (opt.answer) {
      right.push({
        id: `answer-${opt.answer.id}-${opt.id}`, // IMPORTANT: unique id
        answerId: opt.answer.id,
        type: "text",
        value: opt.answer.answer,
      });
    }
  });

  return { left, right };
}

export default function Course() {
  const { t, i18n } = useTranslation();
  const savedLang = localStorage.getItem("lang") || "ar";
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const contentRef = useRef(null);
  const mainRef = useRef(null);
  const lessonRefs = useRef({});
  const dropdownButtonRef = useRef(null); // new ref for the button
  const { user, logout, setUser } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const [courseData, setCourseData] = useState(null);
  const [modules, setModules] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showModuleList, setShowModuleList] = useState(false);
  const [selectedModuleIndex, setSelectedModuleIndex] = useState(0);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [videoWatched, setVideoWatched] = useState(false);
  const [activeTab, setActiveTab] = useState("lesson");
  const [activeSection, setActiveSection] = useState("script");
  const [openModules, setOpenModules] = useState([0]);
  const [isExamCompleted, setIsExamCompleted] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Exam states
  const [lessonQuestions, setLessonQuestions] = useState([]);
  const [summaryQuestions, setSummaryQuestions] = useState([]);
  const [summaryCalculations, setSummaryCalculations] = useState([]);
  const [answersSelected, setAnswersSelected] = useState({});

  const [loadingCourse, setLoadingCourse] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [hintModal, setHintModal] = useState({ open: false, text: "" });
  const toggleModule = (index) => {
    setOpenModules((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  useEffect(() => {
    if (!openModules.includes(selectedModuleIndex)) {
      setOpenModules((prev) => [selectedModuleIndex]);
    }
  }, [selectedModuleIndex]);
  // Fetch course data
  useEffect(() => {
    const fetchCourseData = async () => {
      const lastCourseId = localStorage.getItem("lastCourseId");
      if (!lastCourseId) return;

      setLoadingCourse(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/courses/${lastCourseId}`, {
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        });

        const modulesData = response.data.data.modules || [];

        // Define your local static final exam lesson
        // Create final exam lesson
        const finalExamLesson = {
          id: "final-exam",
          name: "التقييم النهائي",
          isFinalExam: true,
          type: TYPE_FINAL_ASSESSMENT,
        };

        // Create a new module for it
        const finalExamModule = {
          id: "final-module",
          name: "الاختبار النهائي",
          lessons: [finalExamLesson],
          isFinalModule: true,
        };

        // Append as a NEW module
        const updatedModules = [...modulesData, finalExamModule];

        setCourseData(response.data.data.course);
        setModules(updatedModules);
        setIsExamCompleted(response.data.data.is_exam_completed);

        // Find first incomplete lesson
        let found = false;
        updatedModules.forEach((mod, mIndex) => {
          if (found) return;
          const lessonIndex = mod.lessons.findIndex((l) => !l.is_completed);
          if (lessonIndex !== -1) {
            setSelectedModuleIndex(mIndex);
            setCurrentItemIndex(lessonIndex);
            found = true;
          }
        });

        if (!found) {
          setSelectedModuleIndex(0);
          setCurrentItemIndex(0);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          setUser(null);
          navigate("/auth/login", { replace: true });
        } else {
          console.error(err);
        }
      } finally {
        setLoadingCourse(false);
      }
    };

    fetchCourseData();
  }, [user?.token, i18n.language]);

  const currentModule = modules[selectedModuleIndex] || { lessons: [] };
  const currentItem = currentModule.lessons[currentItemIndex];

  // Fetch lesson questions
  const fetchLessonQuestions = async () => {
    setLoadingQuestions(true);

    setLessonQuestions([]);
    setCurrentQuestionId(null);
    setAnswersSelected({});

    if (!currentItem) {
      setLoadingQuestions(false);
      return;
    }
    // Only fetch for assessment lessons
    if (![TYPE_PRE_ASSESSMENT, TYPE_POST_ASSESSMENT, TYPE_IN_ASSESSMENT].includes(currentItem.type)) {
      setLessonQuestions([]);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/lesson-questions/${currentItem.id}`,
        {
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );

      const questions = response.data.data.questions || [];
      setLessonQuestions(questions);

      // Reset selected answers
      setAnswersSelected({});

      // ✅ PRE/POST Step Flow Fix:
      if (currentItem.type === TYPE_PRE_ASSESSMENT || currentItem.type === TYPE_POST_ASSESSMENT) {
        // Find first unsolved question
        const firstUnsolved = questions.find((q) => !q.solution);

        if (firstUnsolved) {
          setCurrentQuestionId(firstUnsolved.id);
        } else {
          setCurrentQuestionId(null); // all solved
        }
      }

      // In-assessment initialization
      if (currentItem.type === TYPE_IN_ASSESSMENT) {
        setLessonQuestions((prev) =>
          prev.map((q) => ({
            ...q,
            answeredCorrectly: false,
          }))
        );
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        console.error(err);
      }
    } finally {
      setLoadingQuestions(false);
    }
  };
  useEffect(() => {
    fetchLessonQuestions();
  }, [currentItem, i18n.language, user?.token]);

  useEffect(() => {
    let isMounted = true; // 🛡️ لمنع race conditions

    const fetchSummaryQuestions = async () => {
      setLoadingQuestions(true);

      // ✅ FIX 1: handle no currentItem
      if (!currentItem) {
        setSummaryQuestions([]);
        setSummaryCalculations(null);
        setLoadingQuestions(false);
        return;
      }

      // ✅ FIX 2: handle non-summary
      if (currentItem.type !== TYPE_SUMMARY) {
        setSummaryQuestions([]);
        setSummaryCalculations(null);
        setLoadingQuestions(false);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/module-summary/${currentModule?.id}`,
          {
            headers: {
              lang: i18n.language,
              Authorization: `Bearer ${user?.token}`,
            },
          }
        );

        if (!isMounted) return; // 🛡️ ignore old response

        setSummaryQuestions(response.data.data || []);
        setSummaryCalculations(response.data.calculations || null);
      } catch (err) {
        if (!isMounted) return;

        if (err.response?.status === 401) {
          setUser(null);
          navigate("/auth/login", { replace: true });
        } else {
          console.error(err);
        }
      } finally {
        if (isMounted) {
          setLoadingQuestions(false);
        }
      }
    };

    fetchSummaryQuestions();

    return () => {
      isMounted = false; // 🛡️ cleanup
    };
  }, [currentItem, i18n.language, user?.token]);
  // Click outside dropdown to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        dropdownButtonRef.current &&
        !dropdownButtonRef.current.contains(event.target)
      ) {
        setShowModuleList(false);
      }
    };

    if (showModuleList) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showModuleList]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const container = contentRef.current;
      if (!container) return;

      const blocks = container.querySelectorAll("blockquote");

      blocks.forEach((block) => {
        if (block.dataset.copyAttached) return;
        block.dataset.copyAttached = "true";
        block.style.position = "relative";
        block.style.cursor = "pointer";

        const tooltip = document.createElement("span");
        tooltip.className = "copy-tooltip";
        tooltip.textContent = "نسخ";
        // tooltip.style.marginTop = "-17px";
        block.appendChild(tooltip);

        block.addEventListener("click", () => {
          // Create temporary textarea to copy content
          const textarea = document.createElement("textarea");
          textarea.value = block.innerText.replace("نسخ", "").trim();
          document.body.appendChild(textarea);
          textarea.select();

          try {
            document.execCommand("copy");
            tooltip.textContent = "تم النسخ ✓";
            setTimeout(() => {
              tooltip.textContent = "نسخ";
            }, 1500);

            const originalBg = block.style.backgroundColor;
            block.style.backgroundColor = "#d1fae5";
            setTimeout(() => {
              block.style.backgroundColor = originalBg || "";
            }, 500);
          } catch (err) {
            console.error("Copy failed", err);
          } finally {
            document.body.removeChild(textarea); // cleanup
          }
        });
      });
    }, 500); // delay to allow content to render

    return () => clearTimeout(timer); // cleanup
  }, [currentItem]);

  useEffect(() => {
    const activeLessonKey = `${selectedModuleIndex}-${currentItemIndex}`;

    if (lessonRefs.current[activeLessonKey]) {
      lessonRefs.current[activeLessonKey].scrollIntoView({
        behavior: "smooth",
        block: "center", // scroll to center
      });
    }
  }, [selectedModuleIndex, currentItemIndex]);

  useEffect(() => {
    const handleClickOutsideUserMenu = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) document.addEventListener("mousedown", handleClickOutsideUserMenu);
    return () => document.removeEventListener("mousedown", handleClickOutsideUserMenu);
  }, [showUserMenu]);

  const downloadFile = async () => {
    try {
      const response = await axios.get(currentItem.file, {
        responseType: "blob",
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      // Extract filename or fallback
      const fileName = currentItem.file.split("/").pop() || "downloaded-file";

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
    }
  };

  const handleSubmitAnswer = async (q, idx) => {
    const selectedAnswer = answersSelected[idx];

    if (!selectedAnswer) return;

    try {
      setSubmittingAnswer(true);

      // ---------------- TYPE 2: Matching question (local only) ----------------
      if (q.type === 2) {
        const normalized = normalizeMatchingQuestion(q);

        let allCorrect = true;

        for (const left of normalized.left) {
          const placed = selectedAnswer[left.id];

          // ❌ Not placed
          if (!placed) {
            allCorrect = false;
            break;
          }

          // ❌ Wrong answer (compare answerId)
          if (placed.answerId !== left.answerId) {
            allCorrect = false;
            break;
          }
        }

        if (allCorrect) {
          // ✅ Mark locally as correct
          setLessonQuestions((prev) =>
            prev.map((quest) => (quest.id === q.id ? { ...quest, solution: { is_correct: true } } : quest))
          );
        } else {
          // ❌ Show hint
          setHintModal({ open: true, text: q.hint });
        }

        return;
      }

      // ---------------- TYPE 1: Normal question submit (existing backend flow) ----------------
      const chosenOption = q.options.find((o) => o.id === selectedAnswer);
      if (!chosenOption) return;

      const payload = {
        answers: [
          {
            question_id: q.id,
            answer_id: selectedAnswer,
          },
        ],
      };

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/question-solution`, payload, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
          "Content-Type": "application/json",
        },
      });

      setLessonQuestions((prev) =>
        prev.map((quest) =>
          quest.id === q.id
            ? {
                ...quest,
                solution: {
                  option_id: chosenOption.id,
                  is_correct: chosenOption.is_correct,
                },
              }
            : quest
        )
      );

      if (!chosenOption.is_correct) {
        setHintModal({ open: true, text: q.hint });
        setAnswersSelected((prev) => ({ ...prev, [idx]: null }));
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        console.error(err);
      }
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const handleCompleteCourse = async () => {
    try {
      await axios.get(`${process.env.REACT_APP_API_BASE_URL}/complete-course/${courseData.id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
          "Content-Type": "application/json",
        },
      });
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        console.error("Submit summary answer error:", err);
      }
    }
  };

  const handleLogout = async () => {
    const courseId = localStorage.getItem("current_exam_course_id");
    const token = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user"))?.token : null;

    if (courseId && token) {
      await finishExamOnLogout(courseId, token);
    }
    localStorage.removeItem("user");
    localStorage.clear();
    setUser(null);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/logout`,
        {},
        {
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
    } catch (err) {
      if (err.response?.status === 401) {
        // Unauthorized: remove user and redirect to login
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        console.error(err);
      }
    } finally {
      logout();
      localStorage.clear();
      navigate("/auth/landing?lang=" + savedLang, { replace: true });
    }
  };

  // Complete lesson
  const completeLesson = async (lessonId) => {
    // ✅ تحقق إذا الدرس مكتمل مسبقًا
    const lessonAlreadyCompleted = modules.some((mod) =>
      mod.lessons.some((l) => l.id === lessonId && l.is_completed)
    );

    if (lessonAlreadyCompleted) {
      return; // 🚫 لا تضرب API
    }

    try {
      await axios.get(`${process.env.REACT_APP_API_BASE_URL}/complete-lesson/${lessonId}`, {
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${user?.token}`,
        },
      });

      // ✅ تحديث state
      setModules((prev) =>
        prev.map((mod) => ({
          ...mod,
          lessons: mod.lessons.map((l) => (l.id === lessonId ? { ...l, is_completed: true } : l)),
        }))
      );
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        console.error(err);
      }
    }
  };
  // Navigation
  const goNextLesson = () => {
    if (currentItemIndex < currentModule.lessons.length - 1) setCurrentItemIndex(currentItemIndex + 1);
    else if (selectedModuleIndex < modules.length - 1) {
      setSelectedModuleIndex(selectedModuleIndex + 1);
      setCurrentItemIndex(0);
    }
    setAnswersSelected({});
    setVideoWatched(false);
    setActiveTab("lesson");
    setActiveSection("script");
    mainRef.current?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const goPrevLesson = () => {
    if (currentItemIndex > 0) setCurrentItemIndex(currentItemIndex - 1);
    else if (selectedModuleIndex > 0) {
      const prevModule = modules[selectedModuleIndex - 1];
      setSelectedModuleIndex(selectedModuleIndex - 1);
      setCurrentItemIndex(prevModule.lessons.length - 1);
    }
    setAnswersSelected({});
    setVideoWatched(false);
    setActiveTab("lesson");
    setActiveSection("script");
    mainRef.current?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Helpers
  const isVideo = (item) => item?.type === TYPE_VIDEO;
  const isPreAssessment = (item) => item?.type === TYPE_PRE_ASSESSMENT;
  const isInAssessment = (item) => item?.type === TYPE_IN_ASSESSMENT;
  const isPostAssessment = (item) => item?.type === TYPE_POST_ASSESSMENT;
  const isDocument = (item) => item?.type === TYPE_DOCUMENT;
  const isSummary = (item) => item?.type === TYPE_SUMMARY;
  const isFinalAssessment = (item) => item?.type === TYPE_FINAL_ASSESSMENT;

  function allDropZonesFilled(question, selectedAnswer) {
    if (!selectedAnswer) return false;

    // Normalize question to get left-side drop zone IDs
    const leftIds = question.options.flatMap((opt) => {
      const ids = [];
      if (opt.option1) ids.push(`left-${opt.id}`);
      if (opt.image1) ids.push(`left-img-${opt.id}`);
      return ids;
    });

    // Check every left ID has a token in selectedAnswer
    return leftIds.every((id) => selectedAnswer[id]);
  }

  const canAccessModule = (moduleIndex) => {
    if (moduleIndex === 0) return true;
    const prevModule = modules[moduleIndex - 1];
    return prevModule.lessons.every((l) => l.is_completed);
  };

  const progress = React.useMemo(() => {
    let totalLessons = 0;
    let completedLessons = 0;

    modules.forEach((mod) => {
      // Exclude final exam from count
      const lessonsWithoutFinal = mod.lessons.filter((l) => l.type !== TYPE_FINAL_ASSESSMENT);
      totalLessons += lessonsWithoutFinal.length;
      completedLessons += lessonsWithoutFinal.filter((l) => l.is_completed).length;
    });

    return totalLessons === 0 ? 0 : Math.round((completedLessons / totalLessons) * 100);
  }, [modules]);

  // Step-by-step Pre/Post exam render
  const renderStepByStepExam = () => {
    if (!currentItem || ![TYPE_PRE_ASSESSMENT, TYPE_POST_ASSESSMENT].includes(currentItem.type)) return null;
    const correctCount = lessonQuestions.filter((q) => q.solution?.is_correct).length;
    const totalCount = lessonQuestions.length;
    const allSolved = lessonQuestions.every((q) => q.solution); // ✅ If all questions solved

    if (loadingQuestions || lessonQuestions.length === 0) {
      return (
        <>
          <div className="flex flex-1 flex-col">
            {/* Header */}
            <div className="mb-4 border-b border-b-black/20 sm:mb-12">
              <div className="flex">
                <button className="border-b-2 border-purple-600 px-4 py-2 font-medium text-purple-600">
                  الاختبار
                </button>
              </div>
            </div>

            {/* Skeleton */}
            <div className="mx-auto w-full max-w-3xl space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-24 w-full animate-pulse rounded-md bg-gray-100" />
              ))}
            </div>
          </div>
          <div className="sticky bottom-0 z-30 flex gap-2 border-t border-black/20 bg-white py-4 pb-8">
            {(currentItemIndex > 0 || selectedModuleIndex > 0) && (
              <button
                onClick={goPrevLesson}
                className="flex min-w-32 items-center justify-center gap-2 rounded-md border border-purple-600 bg-white px-4 py-2 text-purple-600 transition hover:bg-purple-50"
              >
                {i18n.dir() === "ltr" ? <HiArrowLeft /> : <HiArrowRight />} {t("previousLesson")}
              </button>
            )}
            <button
              disabled
              className="mr-auto flex min-w-32 items-center justify-center gap-2 rounded-md bg-purple-500 px-4 py-2 text-white hover:bg-purple-600 disabled:opacity-50"
              onClick={async () => {
                await completeLesson(currentItem.id);
                goNextLesson();
              }}
            >
              {t("nextLesson")}
              {i18n.dir() === "ltr" ? <HiArrowRight /> : <HiArrowLeft />}{" "}
            </button>
          </div>
        </>
      );
    }

    if (allSolved) {
      return (
        <>
          <div className="flex flex-1 flex-col">
            <div className="mb-4 border-b border-b-black/20 sm:mb-12">
              <div className="flex">
                <button className={`border-b-2 border-purple-600 px-4 py-2 font-medium text-purple-600`}>
                  الاختبار
                </button>
              </div>
            </div>

            <div className="mx-auto w-full max-w-3xl space-y-8 pb-8">
              <div className="rounded-md border bg-green-50 p-4 font-semibold text-green-500">
                {t("youAlreadyCompletedThisAssessment")}

                {TYPE_POST_ASSESSMENT === currentItem.type && (
                  <p className="text-sm font-bold">
                    الإجابات الصحيحة: {correctCount} / {totalCount}
                  </p>
                )}
              </div>

              {lessonQuestions.map((q, idx) => {
                const optionDir = q.options.every((o) => isCodeLTR(o.option || "")) ? "ltr" : "rtl";
                return (
                  <div key={q.id} className="mb-6 bg-white">
                    {/* Question number */}
                    <div className="mb-2 text-right text-sm text-gray-500">
                      السؤال {idx + 1} من {lessonQuestions.length}
                    </div>

                    {/* Question text */}
                    <h3 className="mb-4 whitespace-pre-line text-2xl font-bold leading-normal text-gray-800">
                      {
                        <div
                          dangerouslySetInnerHTML={{
                            __html: q?.text?.replace(/\r\n/g, "<br />"),
                          }}
                        />
                      }
                    </h3>

                    {/* Options (read-only) */}
                    <div className="flex flex-col gap-2">
                      {q.options.map((option) => {
                        const isSelected = option.id === q.solution?.option_id;
                        const isCorrect = q.solution?.is_correct;

                        let classes =
                          "rounded-md border px-4 py-3 font-medium flex justify-between items-center";

                        if (TYPE_POST_ASSESSMENT === currentItem.type && isSelected) {
                          classes += isCorrect
                            ? " border-green-400 bg-green-100 text-green-800"
                            : " border-red-400 bg-red-100 text-red-800";
                        } else if (isSelected) {
                          classes += " border-purple-400 bg-purple-100 text-purple-800";
                        } else {
                          classes += " border-gray-200 bg-gray-50 text-gray-700";
                        }

                        return (
                          <div key={option.id} className={classes}>
                            <span
                              className={`flex-1 whitespace-pre-line break-words ${optionDir === "ltr" ? "text-left" : "text-right"} `}
                              dir={optionDir}
                              style={{
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {option.option}
                            </span>
                            {/* ✅ Show result ONLY for post assessment */}
                            {TYPE_POST_ASSESSMENT === currentItem.type && isSelected && (
                              <span className="text-sm font-bold">{isCorrect ? "✔" : "✖"}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="sticky bottom-0 z-30 flex gap-2 border-t border-black/20 bg-white py-4 pb-8">
            {(currentItemIndex > 0 || selectedModuleIndex > 0) && (
              <button
                onClick={goPrevLesson}
                className="flex min-w-32 items-center justify-center gap-2 rounded-md border border-purple-600 bg-white px-4 py-2 text-purple-600 hover:bg-purple-50"
              >
                {i18n.dir() === "ltr" ? <HiArrowLeft /> : <HiArrowRight />}
                {t("previousLesson")}
              </button>
            )}

            <button
              onClick={async () => {
                await completeLesson(currentItem.id);
                goNextLesson();
              }}
              className="mr-auto flex min-w-32 items-center justify-center gap-2 rounded-md bg-purple-500 px-4 py-2 text-white hover:bg-purple-600"
            >
              {t("nextLesson")}
              {i18n.dir() === "ltr" ? <HiArrowRight /> : <HiArrowLeft />}
            </button>
          </div>
        </>
      );
    }

    const question = lessonQuestions.find((q) => q.id === currentQuestionId);

    if (!question) return null;

    const selectedAnswer = answersSelected[question.id];

    // Remaining unsolved questions
    const remaining = lessonQuestions.filter((q) => !q.solution);

    // Last question?
    const isLastQuestion = remaining.length === 1;

    const idx = lessonQuestions.findIndex((q) => q.id === currentQuestionId);

    return (
      <>
        <div className="flex flex-1 flex-col">
          <div className="mb-4 border-b border-b-black/20 sm:mb-12">
            <div className="flex">
              <div className="border-b-2 border-purple-600 px-4 py-2 font-medium text-purple-600 transition">
                الاختبار
              </div>
            </div>
          </div>
          {currentItem.description && (
            <>
              <div
                style={{
                  backgroundColor: "#f8f9fa", // Bootstrap's light grey
                  padding: "1rem",
                  borderRadius: "4px",
                  border: "1px solid #dee2e6", // Optional: adds a subtle border
                }}
                dangerouslySetInnerHTML={{
                  __html: currentItem.description?.replace(/\r\n/g, "<br />"),
                }}
              />
              <br />
            </>
          )}
          {/* Question */}
          <div className="mx-auto w-full max-w-3xl flex-1 flex-grow space-y-4 pb-4 transition-all duration-300 xl:pb-8 xl:pt-0">
            {/* Question counter */}
            <div className="mb-2 text-right text-sm text-gray-500">
              السؤال {idx + 1} من {lessonQuestions.length}
            </div>
            <div className="rounded-md bg-white">
              <h3 className="mb-4 whitespace-pre-line text-2xl font-bold leading-normal text-gray-800">
                {
                  <div
                    dangerouslySetInnerHTML={{
                      __html: question?.text?.replace(/\r\n/g, "<br />"),
                    }}
                  />
                }
              </h3>

              {/* Options */}
              <div className="flex flex-col gap-2">
                {(() => {
                  const optionDir = question.options.every((o) => isCodeLTR(o.option || "")) ? "ltr" : "rtl";
                  return question.options.map((option) => (
                    <label
                      key={option.id}
                      className={`flex items-center gap-3 rounded-md p-3 font-medium transition ${
                        selectedAnswer === option.option ? "" : "cursor-pointer"
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${question.id}`}
                        value={option.option}
                        checked={selectedAnswer === option.option}
                        onChange={() =>
                          setAnswersSelected((prev) => ({
                            ...prev,
                            [question.id]: option.option,
                          }))
                        }
                        className="h-6 w-6 cursor-pointer accent-purple-500"
                      />
                      <span
                        className={`flex-1 whitespace-pre-line break-words ${optionDir === "ltr" ? "text-left" : "text-right"} `}
                        dir={optionDir}
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {option.option}
                      </span>
                    </label>
                  ));
                })()}
              </div>

              {/* Submit Button */}
              <button
                disabled={!selectedAnswer || submittingAnswer}
                onClick={async () => {
                  if (!selectedAnswer) return;

                  setSubmittingAnswer(true);

                  const answerObj = question.options.find((o) => o.option === selectedAnswer);

                  if (!answerObj) return;

                  try {
                    await axios.post(
                      `${process.env.REACT_APP_API_BASE_URL}/question-solution`,
                      {
                        answers: [
                          {
                            question_id: question.id,
                            answer_id: answerObj.id,
                          },
                        ],
                      },
                      {
                        headers: {
                          Authorization: `Bearer ${user?.token}`,
                          "Content-Type": "application/json",
                        },
                      }
                    );

                    await fetchLessonQuestions();
                  } catch (err) {
                    if (err.response?.status === 401) {
                      setUser(null);
                      navigate("/auth/login", { replace: true });
                    } else {
                      console.error(err);
                    }
                  } finally {
                    setSubmittingAnswer(false);
                  }
                }}
                className="mt-6 rounded-md bg-purple-500 px-4 py-2 text-white transition hover:bg-purple-600 disabled:opacity-50"
              >
                {submittingAnswer ? t("sending...") : isLastQuestion ? t("Submit") : t("nextQuestion")}
              </button>
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 z-30 flex gap-2 border-t border-black/20 bg-white py-4 pb-8">
          {(currentItemIndex > 0 || selectedModuleIndex > 0) && (
            <button
              onClick={goPrevLesson}
              className="flex min-w-32 items-center justify-center gap-2 rounded-md border border-purple-600 bg-white px-4 py-2 text-purple-600 transition hover:bg-purple-50"
            >
              {i18n.dir() === "ltr" ? <HiArrowLeft /> : <HiArrowRight />} {t("previousLesson")}
            </button>
          )}
          <button
            disabled={remaining.length > 0}
            className="mr-auto flex min-w-32 items-center justify-center gap-2 rounded-md bg-purple-500 px-4 py-2 text-white hover:bg-purple-600 disabled:opacity-50"
            onClick={async () => {
              await completeLesson(currentItem.id);
              goNextLesson();
            }}
          >
            {t("nextLesson")}
            {i18n.dir() === "ltr" ? <HiArrowRight /> : <HiArrowLeft />}{" "}
          </button>
        </div>
      </>
    );
  };

  // In-Assessment render
  const renderInAssessment = () => {
    const alreadySolved = lessonQuestions.length > 0 && lessonQuestions.every((q) => q.solution?.is_correct);
    if (!currentItem || currentItem.type !== TYPE_IN_ASSESSMENT) return null;
    // ✅ correct calculation AFTER data ready
    // ✅ prevent wrong render before data ready
    if (loadingQuestions || lessonQuestions.length === 0) {
      return (
        <>
          <div className="flex flex-1 flex-col">
            <div className="mb-4 border-b border-b-black/20 sm:mb-12">
              <div className="flex">
                <div className="border-b-2 border-purple-600 px-4 py-2 font-medium text-purple-600">
                  الاختبار
                </div>
              </div>
            </div>

            <div className="mx-auto w-full max-w-3xl space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-24 w-full animate-pulse rounded-md bg-gray-100" />
              ))}
            </div>
          </div>
          {/* Navigation */}
          <div className="bottom-0 z-30 flex gap-2 border-t border-black/20 bg-white py-4 pb-8">
            {(currentItemIndex > 0 || selectedModuleIndex > 0) && (
              <button
                onClick={goPrevLesson}
                className="flex min-w-32 items-center justify-center gap-2 rounded-md border border-purple-600 bg-white px-4 py-2 text-purple-600 hover:bg-purple-50"
              >
                {i18n.dir() === "ltr" ? <HiArrowLeft /> : <HiArrowRight />}
                {t("previousLesson")}
              </button>
            )}

            <button
              disabled
              onClick={async () => {
                await completeLesson(currentItem.id);
                goNextLesson();
              }}
              className="mr-auto flex min-w-32 items-center justify-center gap-2 rounded-md bg-purple-500 px-4 py-2 text-white hover:bg-purple-600 disabled:opacity-50"
            >
              {t("nextLesson")}
              {i18n.dir() === "ltr" ? <HiArrowRight /> : <HiArrowLeft />}
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="flex flex-1 flex-col">
          <div className="mb-4 border-b border-b-black/20 sm:mb-12">
            <div className="flex">
              <div className="border-b-2 border-purple-600 px-4 py-2 font-medium text-purple-600">
                الاختبار
              </div>
            </div>
          </div>

          <div className="mx-auto w-full max-w-3xl flex-1 flex-grow space-y-8 pb-4 xl:pb-8 xl:pt-0">
            {lessonQuestions.map((q, idx) => {
              const selectedAnswer = answersSelected[idx];
              const answeredCorrectly = q.solution?.is_correct === true;
              const optionDir = q.options.every((o) => isCodeLTR(o.option || "")) ? "ltr" : "rtl";

              return (
                <div key={q.id} className="bg-white">
                  {/* Question counter */}
                  <div className="mb-2 text-right text-sm text-gray-500">
                    السؤال {idx + 1} من {lessonQuestions.length}
                  </div>

                  {/* Question text */}
                  <h3 className="mb-4 whitespace-pre-line text-2xl font-bold leading-normal text-gray-800">
                    {
                      <div
                        dangerouslySetInnerHTML={{
                          __html: q?.text?.replace(/\r\n/g, "<br />"),
                        }}
                      />
                    }
                  </h3>

                  {/* MCQ */}
                  {q.type === 1 && (
                    <div className="flex flex-col gap-3">
                      {q.options.map((option) => {
                        const userAnswerId = q.solution?.option_id;
                        const isUserAnswer = option.id === userAnswerId;
                        const isSelected = selectedAnswer === option.id;

                        // ✅ REVIEW MODE
                        if (answeredCorrectly || currentItem.is_completed) {
                          return (
                            <div
                              key={option.id}
                              className={`rounded-md border px-4 py-3 font-medium ${
                                isUserAnswer
                                  ? "border-purple-400 bg-purple-100 text-purple-800"
                                  : "border-gray-200 bg-gray-50 text-gray-700"
                              }`}
                            >
                              <span
                                className={`flex-1 whitespace-pre-line break-words ${optionDir === "ltr" ? "text-left" : "text-right"} `}
                                dir={optionDir}
                                style={{
                                  wordBreak: "break-word",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {option.option}
                              </span>{" "}
                            </div>
                          );
                        }

                        // ✅ ANSWERING MODE
                        return (
                          <label
                            key={option.id}
                            className="flex cursor-pointer items-center gap-3 rounded-md p-3 font-medium transition hover:bg-purple-50"
                          >
                            <input
                              type="radio"
                              checked={isSelected}
                              onChange={() =>
                                setAnswersSelected((prev) => ({
                                  ...prev,
                                  [idx]: option.id,
                                }))
                              }
                              className="h-6 w-6 accent-purple-500"
                            />
                            <span
                              className={`flex-1 whitespace-pre-line break-words ${optionDir === "ltr" ? "text-left" : "text-right"} `}
                              dir={optionDir}
                              style={{
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {option.option}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {/* Matching */}
                  {q.type === 2 && (
                    <MatchingQuestion
                      isCompleted={currentItem.is_completed}
                      question={q}
                      value={answersSelected[idx]}
                      onChange={(val) =>
                        setAnswersSelected((prev) => ({
                          ...prev,
                          [idx]: val,
                        }))
                      }
                    />
                  )}

                  {/* Submit */}
                  {!answeredCorrectly &&
                    selectedAnswer &&
                    allDropZonesFilled(q, selectedAnswer) &&
                    !currentItem.is_completed && (
                      <button
                        onClick={() => handleSubmitAnswer(q, idx)}
                        className="mt-5 min-w-32 rounded-md bg-purple-600 py-2 font-semibold text-white hover:bg-purple-700"
                      >
                        {submittingAnswer ? t("sending...") : t("Submit")}
                      </button>
                    )}

                  {/* Correct Message */}
                  {(answeredCorrectly || currentItem.is_completed) && (
                    <p className="mt-4 flex items-center gap-2 font-semibold text-green-600">
                      <FaCheck /> <span>{t("correctAnswer")}</span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="bottom-0 z-30 flex gap-2 border-t border-black/20 bg-white py-4 pb-8">
          {(currentItemIndex > 0 || selectedModuleIndex > 0) && (
            <button
              onClick={goPrevLesson}
              className="flex min-w-32 items-center justify-center gap-2 rounded-md border border-purple-600 bg-white px-4 py-2 text-purple-600 hover:bg-purple-50"
            >
              {i18n.dir() === "ltr" ? <HiArrowLeft /> : <HiArrowRight />}
              {t("previousLesson")}
            </button>
          )}

          <button
            disabled={!alreadySolved && !currentItem.is_completed}
            onClick={async () => {
              await completeLesson(currentItem.id);
              goNextLesson();
            }}
            className="mr-auto flex min-w-32 items-center justify-center gap-2 rounded-md bg-purple-500 px-4 py-2 text-white hover:bg-purple-600 disabled:opacity-50"
          >
            {t("nextLesson")}
            {i18n.dir() === "ltr" ? <HiArrowRight /> : <HiArrowLeft />}
          </button>
        </div>
      </>
    );
  };

  const renderSummary = () => {
    if (!currentItem || currentItem.type !== TYPE_SUMMARY) return null;

    // ✅ Loading OR data not ready
    if (loadingQuestions || !summaryCalculations) {
      return (
        <div className="flex flex-1 flex-col">
          <div className="mb-4 border-b border-b-black/20 sm:mb-12"></div>

          <div className="mx-auto w-full max-w-3xl space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 animate-pulse rounded-md bg-gray-100" />
            ))}
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="flex flex-1 flex-col">
          <div className="mb-4 border-b border-b-black/20 sm:mb-12"></div>

          {/* ===== Calculations Summary ===== */}
          <div className="mx-auto w-full max-w-3xl space-y-8 rounded-xl border border-purple-100 bg-purple-50/40 p-6 pb-8">
            {/* Percentages */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg bg-white p-4 text-center shadow-sm">
                <p className="text-sm text-gray-500">النسبة القبلية</p>
                <p className="text-2xl font-bold text-purple-700">{summaryCalculations.pre_percentage}%</p>
              </div>

              <div className="rounded-lg bg-white p-4 text-center shadow-sm">
                <p className="text-sm text-gray-500">النسبة البعدية</p>
                <p className="text-2xl font-bold text-purple-700">{summaryCalculations.post_percentage}%</p>
              </div>

              <div className="rounded-lg bg-white p-4 text-center shadow-sm">
                <p className="text-sm text-gray-500">نسبة التحسن</p>
                <p
                  className={`text-2xl font-bold ${
                    summaryCalculations.improvement_percentage > 0 ? "text-green-600" : "text-gray-700"
                  }`}
                >
                  {Math.max(0, summaryCalculations.improvement_percentage)}%
                </p>
              </div>
            </div>

            {/* Skills Already Improved */}
            {summaryCalculations.skills_already_improve?.length > 0 && (
              <div>
                <h3 className="mb-3 text-lg font-bold text-green-700">المهارات التي تم إتقانها</h3>
                <div className="space-y-2">
                  {summaryCalculations.skills_already_improve.map((skill, index) => (
                    <div
                      key={index}
                      className="rounded-md border border-green-200 bg-green-100 px-4 py-2 text-green-800"
                    >
                      {skill.title}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skills Must Improve */}
            {summaryCalculations.skills_must_improve?.length > 0 && (
              <div>
                <h3 className="mb-3 text-lg font-bold text-red-700">المهارات التي تحتاج إلى تحسين</h3>
                <div className="space-y-2">
                  {summaryCalculations.skills_must_improve.map((skill, index) => (
                    <div
                      key={index}
                      className="rounded-md border border-red-200 bg-red-100 px-4 py-2 text-red-800"
                    >
                      {skill.title}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Optional Questions Review (if you want later) */}
          <div className="mx-auto w-full max-w-3xl flex-1 flex-grow space-y-8 pb-4 xl:pb-8 xl:pt-0">
            {/* يمكنك تفعيل عرض الأسئلة هنا لاحقاً */}
          </div>
        </div>

        {/* ===== Navigation Buttons ===== */}
        <div className="sticky bottom-0 z-30 flex gap-2 border-t border-black/20 bg-white py-4 pb-8">
          {(currentItemIndex > 0 || selectedModuleIndex > 0) && (
            <button
              onClick={goPrevLesson}
              className="flex min-w-32 items-center justify-center gap-2 rounded-md border border-purple-600 bg-white px-4 py-2 text-purple-600 transition hover:bg-purple-50"
            >
              {i18n.dir() === "ltr" ? <HiArrowLeft /> : <HiArrowRight />}
              {t("previousLesson")}
            </button>
          )}

          <button
            onClick={async () => {
              const currentModule = modules[selectedModuleIndex];
              const currentLesson = currentModule.lessons[currentItemIndex];

              // آخر summary في الكورس
              let lastSummaryIndexInCourse = { mIdx: -1, lIdx: -1 };
              modules.forEach((mod, mIdx) => {
                mod.lessons.forEach((lesson, lIdx) => {
                  if (lesson.type === TYPE_SUMMARY) {
                    lastSummaryIndexInCourse = { mIdx, lIdx };
                  }
                });
              });

              const isLastSummaryInCourse =
                lastSummaryIndexInCourse.mIdx === selectedModuleIndex &&
                lastSummaryIndexInCourse.lIdx === currentItemIndex;

              await completeLesson(currentLesson.id);

              if (isLastSummaryInCourse) {
                await handleCompleteCourse();
              }

              goNextLesson();
            }}
            className="mr-auto flex min-w-32 items-center justify-center gap-2 rounded-md bg-purple-500 px-4 py-2 text-white hover:bg-purple-600"
          >
            {(() => {
              const currentModule = modules[selectedModuleIndex];

              const lastSummaryIndexInModule = currentModule.lessons
                .map((l, idx) => ({ l, idx }))
                .filter((x) => x.l.type === TYPE_SUMMARY)
                .map((x) => x.idx)
                .pop();

              let lastSummaryIndexInCourse = { mIdx: -1, lIdx: -1 };
              modules.forEach((mod, mIdx) => {
                mod.lessons.forEach((lesson, lIdx) => {
                  if (lesson.type === TYPE_SUMMARY) {
                    lastSummaryIndexInCourse = { mIdx, lIdx };
                  }
                });
              });

              const isLastSummaryInModule = currentItemIndex === lastSummaryIndexInModule;

              const isLastSummaryInCourse =
                lastSummaryIndexInCourse.mIdx === selectedModuleIndex &&
                lastSummaryIndexInCourse.lIdx === currentItemIndex;

              if (isLastSummaryInCourse) return "الانتقال الى الاختبار النهائي";
              if (isLastSummaryInModule) return "الانتقال الى الوحدة التالية";

              return t("nextLesson");
            })()}

            {i18n.dir() === "ltr" ? <HiArrowRight /> : <HiArrowLeft />}
          </button>
        </div>
      </>
    );
  };

  // Inside the Course component render
  const renderSidebarContent = () => {
    if (loadingCourse) {
      // Sidebar skeleton
      return (
        <div className="space-y-3 px-4 pt-6">
          <div className="h-20 animate-pulse rounded-md bg-gray-100" />
          <div className="h-20 animate-pulse rounded-md bg-gray-100" />
        </div>
      );
    }

    return (
      <>
        <div className="m-4 mt-8 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {(() => {
              const radius = 22;
              const stroke = 4;
              const normalizedRadius = radius - stroke * 0.5;
              const circumference = normalizedRadius * 2 * Math.PI;
              const strokeDashoffset = circumference - (progress / 100) * circumference;

              return (
                <svg height={radius * 2} width={radius * 2}>
                  {/* Rotate only the circles */}
                  <g transform={`rotate(-90 ${radius} ${radius})`}>
                    {/* Background circle */}
                    <circle
                      stroke="#e5e7eb"
                      fill="transparent"
                      strokeWidth={stroke}
                      r={normalizedRadius}
                      cx={radius}
                      cy={radius}
                    />

                    {/* Progress circle */}
                    <circle
                      stroke="#4432ad"
                      fill="transparent"
                      strokeWidth={stroke}
                      strokeLinecap="round"
                      strokeDasharray={`${circumference} ${circumference}`}
                      strokeDashoffset={strokeDashoffset}
                      r={normalizedRadius}
                      cx={radius}
                      cy={radius}
                      className="transition-all duration-500"
                    />
                  </g>

                  {/* Percentage text stays horizontal */}
                  <text
                    x="50%"
                    y="53%"
                    dominantBaseline="middle"
                    textAnchor="middle"
                    fill="gray"
                    fontSize="12"
                    fontWeight=""
                  >
                    {progress}%
                  </text>
                </svg>
              );
            })()}

            <Link to={`/course-overview/${courseData?.id}`}>
              <h2 className="text-xl">{courseData?.name}</h2>
            </Link>
          </div>
          {/* Sidebar toggle button for mobile */}
          <button
            onClick={() => setSidebarOpen((prev) => !prev)}
            className="rounded-md bg-gray-100 p-1 text-gray-700 hover:bg-gray-200 rtl:rotate-180"
          >
            <FaChevronLeft />
          </button>
        </div>

        <div className="relative space-y-2 px-4 py-3">
          <div className="space-y-2">
            {modules.map((module, moduleIndex) => {
              const locked = !canAccessModule(moduleIndex);

              // ✅ SPECIAL UI FOR FINAL MODULE
              if (module.isFinalModule) {
                const lesson = module.lessons[0]; // only one lesson

                const isActive = selectedModuleIndex === moduleIndex && currentItemIndex === 0;

                return (
                  <div key={moduleIndex} className="rounded-md border">
                    <button
                      disabled={locked}
                      onClick={() => {
                        if (locked) return;

                        setSelectedModuleIndex(moduleIndex);
                        setCurrentItemIndex(0);
                        setActiveTab("lesson");

                        mainRef.current?.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                      }}
                      className={`flex w-full items-center justify-between px-4 py-4 text-sm font-semibold transition ${
                        locked ? "cursor-not-allowed opacity-40" : "hover:bg-purple-50"
                      } ${isActive ? "bg-purple-100 text-purple-800" : "bg-white text-gray-800"}`}
                    >
                      {/* LEFT */}
                      <span className="flex items-center gap-2"> {lesson.name}</span>

                      {/* RIGHT ICON */}
                      {locked ? (
                        <HiOutlineLockClosed className="h-5 w-5 text-purple-500" />
                      ) : (
                        <div></div>
                        // <FaChevronLeft className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                );
              }
              const isOpen = openModules.includes(moduleIndex);

              return (
                <div key={moduleIndex} className="overflow-hidden rounded-md border">
                  {/* MODULE HEADER */}
                  <button
                    disabled={locked}
                    onClick={() => {
                      if (!locked) toggleModule(moduleIndex);
                    }}
                    className={`group flex w-full items-start justify-between gap-4 px-4 py-4 text-sm font-semibold transition-all duration-200 ${locked ? "cursor-not-allowed opacity-40" : "hover:bg-purple-50"} ${isOpen ? "bg-purple-100 text-purple-800" : "bg-white text-gray-800"} `}
                  >
                    {/* Left Content */}
                    <span className="flex items-center gap-2 text-right">
                      {t("modulelabel")} {moduleIndex + 1}: {module.name} {module.isFinalModule && " 🎓"}
                    </span>

                    {/* Right Icon */}
                    {locked ? (
                      <HiOutlineLockClosed className="h-5 w-5 text-purple-500" />
                    ) : (
                      <HiOutlineChevronDown
                        className={`h-5 w-5 transform transition-transform duration-300 ${isOpen ? "rotate-180" : "rotate-0"} `}
                      />
                    )}
                  </button>

                  {/* LESSONS */}
                  {isOpen && (
                    <div className="space-y-1 border-t bg-gray-50 p-2">
                      {module.lessons.map((lesson, lessonIndex) => {
                        const isActive =
                          selectedModuleIndex === moduleIndex && currentItemIndex === lessonIndex;

                        const lessonLocked =
                          !lesson.is_completed &&
                          !module.lessons.slice(0, lessonIndex).every((l) => l.is_completed);

                        const lessonKey = `${moduleIndex}-${lessonIndex}`; // unique key for ref

                        return (
                          <button
                            key={lessonIndex}
                            ref={(el) => (lessonRefs.current[lessonKey] = el)}
                            disabled={lessonLocked}
                            onClick={() => {
                              if (lessonLocked) return;
                              setSelectedModuleIndex(moduleIndex);
                              setCurrentItemIndex(lessonIndex);
                              setActiveTab("lesson");
                              // Scroll to top of the page
                              mainRef.current?.scrollTo({
                                top: 0,
                                behavior: "smooth",
                              });
                            }}
                            className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm ${isActive ? "bg-purple-100 text-purple-700" : "hover:bg-purple-50"} ${lessonLocked ? "cursor-not-allowed opacity-40" : ""} `}
                          >
                            <div className="flex h-6 min-w-6 items-center justify-center rounded-full bg-purple-200">
                              {lesson.is_completed ? (
                                <FaCheck className="h-3 w-3 text-purple-600" />
                              ) : lessonLocked ? (
                                <HiLockClosed className="h-3 w-3 text-purple-500" />
                              ) : (
                                <div className="h-2.5 w-2.5 rounded-full bg-purple-400" />
                              )}
                            </div>

                            <span className="text-right">{lesson.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  const renderLessonContent = () => {
    if (loadingCourse || !currentItem) {
      return (
        <div className="space-y-4">
          <div className="h-6 w-1/3 animate-pulse rounded-md bg-gray-100" />
          <div className="h-64 w-full animate-pulse rounded-md bg-gray-100" />
          <div className="h-4 w-full animate-pulse rounded-md bg-gray-100" />
          <div className="h-4 w-5/6 animate-pulse rounded-md bg-gray-100" />
        </div>
      );
    }

    if (isPreAssessment(currentItem) || isPostAssessment(currentItem)) {
      return renderStepByStepExam();
    }

    if (isInAssessment(currentItem)) {
      return renderInAssessment();
    }

    if (isVideo(currentItem)) {
      const canProceed = videoWatched || currentItem?.is_completed;

      return (
        <div className="flex flex-1 flex-col">
          {/* Tabs */}
          <div className="mb-4 border-b border-b-black/20 sm:mb-12">
            <div className="flex">
              <button
                className={`px-4 py-2 font-medium transition ${
                  activeTab === "lesson"
                    ? "border-b-2 border-purple-600 text-purple-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
                onClick={() => setActiveTab("lesson")}
              >
                الدرس
              </button>
              <button
                className={`px-4 py-2 font-medium transition ${
                  activeTab === "video"
                    ? "border-b-2 border-purple-600 text-purple-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
                onClick={() => setActiveTab("video")}
              >
                الفيديو
              </button>

              {currentItem.file && (
                <button
                  className={`px-4 py-2 font-medium transition ${
                    activeTab === "attachments"
                      ? "border-b-2 border-purple-600 text-purple-600"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                  onClick={() => setActiveTab("attachments")}
                >
                  المرفقات
                </button>
              )}
            </div>
          </div>

          {activeTab === "lesson" && (
            <div className="mx-auto mb-2 w-full max-w-3xl flex-grow">
              <div
                className="prose mx-auto flex-grow text-right"
                dangerouslySetInnerHTML={{
                  __html: currentItem.description?.replace(/\r\n/g, "<br />"),
                }}
              />

              <button
                onClick={() => setActiveTab("video")}
                className="my-4 rounded-md bg-purple-500 px-4 py-2 text-white hover:bg-purple-600"
              >
                مشاهدة الفيديو
              </button>
            </div>
          )}
          {activeTab === "video" && (
            <div className="mx-auto w-full max-w-2xl flex-grow space-y-6">
              <CustomVideoPlayer currentItem={currentItem} onComplete={() => setVideoWatched(true)} />
              {currentItem.script ? (
                <div>
                  <div className="flex border-b border-gray-200">
                    {currentItem.script && (
                      <button
                        onClick={() => setActiveSection("script")}
                        className={`flex-1 px-4 py-2 ${activeSection === "script" ? "border-b-2 border-purple-500 text-purple-600" : "text-gray-500"}`}
                      >
                        نص الفيديو
                      </button>
                    )}
                  </div>

                  <div className="p-4">
                    {activeSection === "script" && (
                      <div
                        className="prose mx-auto flex-grow text-right"
                        dangerouslySetInnerHTML={{
                          __html: currentItem.script?.replace(/\r\n/g, "<br />"),
                        }}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div></div>
              )}

              {/* Code */}
              {currentItem.code && (
                <div className="relative mt-5 flex flex-col">
                  <CodeViewer code={currentItem.code} />
                </div>
              )}
            </div>
          )}

          {activeTab === "attachments" && (
            <div className="relative mx-auto flex w-full max-w-3xl flex-grow flex-col xl:mt-5">
              <div className="space-y-4 rounded-md border bg-gray-50 p-4 shadow">
                <h2 className="text-lg font-semibold text-purple-600">{t("lessonAttachments")}</h2>

                <button
                  onClick={downloadFile}
                  className="inline-flex items-center gap-2 rounded-md bg-purple-500 px-4 py-2 text-white hover:bg-purple-600"
                >
                  <FaFileDownload />
                  {t("download")}
                </button>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="sticky bottom-0 z-30 flex gap-2 border-t border-black/20 bg-white py-4 pb-8">
            {(currentItemIndex > 0 || selectedModuleIndex > 0) && (
              <button
                onClick={goPrevLesson}
                className="flex min-w-32 items-center justify-center gap-2 rounded-md border border-purple-600 bg-white px-4 py-2 text-purple-600 transition hover:bg-purple-50"
              >
                {i18n.dir() === "ltr" ? <HiArrowLeft /> : <HiArrowRight />}
                {t("previousLesson")}
              </button>
            )}

            <div
              className="relative mr-auto"
              onMouseEnter={() => !canProceed && setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <button
                disabled={!canProceed}
                onClick={async () => {
                  if (!canProceed) {
                    setShowTooltip(true);
                    setTimeout(() => setShowTooltip(false), 2000);
                    return;
                  }

                  if (!currentItem.is_completed) {
                    await completeLesson(currentItem.id);
                  }
                  goNextLesson();
                }}
                className="mr-auto flex h-full min-w-32 items-center justify-center gap-2 rounded-md bg-purple-500 px-4 py-2 text-white transition hover:bg-purple-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("nextLesson")}
                {i18n.dir() === "ltr" ? <HiArrowRight /> : <HiArrowLeft />}
              </button>

              {showTooltip && !canProceed && (
                <div className="absolute -top-8 left-[99px] z-50 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-3 py-1 text-sm font-semibold text-white shadow-lg">
                  يجب مشاهدة الفيديو للمتابعة
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (isDocument(currentItem)) {
      return (
        <div className="flex flex-1 flex-col">
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto pb-40">
            <div className="mb-12 border-b border-black/20">
              <div className="flex"></div>
            </div>

            <div
              ref={contentRef}
              className="prose lesson-content mx-auto max-w-3xl p-4 text-right"
              dangerouslySetInnerHTML={{
                __html: currentItem.document.replace(/\r\n/g, "<br />"),
              }}
            />

            {currentItem?.documents?.length > 0 && (
              <div className="mx-4 mt-6 rounded-md border border-black/10 bg-gray-50 p-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-700">المرفقات</h3>

                <div className="flex flex-col gap-2">
                  {currentItem.documents.map((doc, idx) => (
                    <a
                      key={idx}
                      href={doc?.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-purple-600 transition hover:bg-purple-50"
                    >
                      <span className="truncate">{doc?.name}</span>

                      <span className="text-xs text-gray-500">
                        <FaDownload />
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sticky Footer */}
          <div className="sticky bottom-0 z-30 flex gap-2 border-t border-black/20 bg-white py-4 pb-8">
            {(currentItemIndex > 0 || selectedModuleIndex > 0) && (
              <button
                onClick={goPrevLesson}
                className="flex min-w-32 items-center justify-center gap-2 rounded-md border border-purple-600 bg-white px-4 py-2 text-purple-600 transition hover:bg-purple-50"
              >
                {i18n.dir() === "ltr" ? <HiArrowLeft /> : <HiArrowRight />}
                {t("previousLesson")}
              </button>
            )}

            <button
              onClick={async () => {
                await completeLesson(currentItem.id);
                goNextLesson();
              }}
              className="mr-auto flex min-w-32 items-center justify-center gap-2 rounded-md bg-purple-500 px-4 py-2 text-white transition hover:bg-purple-600"
            >
              {t("nextLesson")}
              {i18n.dir() === "ltr" ? <HiArrowRight /> : <HiArrowLeft />}
            </button>
          </div>
        </div>
      );
    }

    if (isSummary(currentItem)) {
      return renderSummary();
    }

    if (isFinalAssessment(currentItem)) {
      return (
        <FinalAssessment
          courseData={courseData}
          isExamCompleted={isExamCompleted}
          onRetake={() => setIsExamCompleted(false)}
        />
      );
    }

    return null;
  };

  return (
    <div className="flex h-screen flex-col bg-[#FEFEFE] text-gray-900">
      {/* Header */}
      <header className="flex w-full flex-shrink-0 items-center justify-between border-b bg-[#FEFEFE] px-3 py-1 text-black xl:px-6">
        <button
          onClick={() => navigate("/?lang=" + savedLang)}
          className="group flex items-center focus:outline-none"
        >
          <img src="/logo-blue.svg" alt="Logo" className="h-10 w-10 sm:h-14 sm:w-14" />
        </button>
        <div className="relative flex items-center justify-end gap-0">
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu((prev) => !prev)}
              className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-gray-200 text-sm font-medium text-gray-800 transition hover:ring-1 hover:ring-purple-300"
            >
              <span>
                {(() => {
                  const first = user?.first_name?.trim() || "";
                  const last = user?.last_name?.trim() || "";

                  if (!first && !last) return "U";

                  const firstInitial = first.charAt(0);
                  const lastInitial = last.charAt(0);

                  // Arabic character detection
                  const isArabic = /[\u0600-\u06FF]/.test(firstInitial);

                  if (isArabic) {
                    return firstInitial; // Only first letter if Arabic
                  }

                  return `${firstInitial}${lastInitial}`.toUpperCase();
                })()}
              </span>
            </button>

            {showUserMenu && (
              <div className="absolute left-0 top-full z-[9999] mt-2 w-32 rounded-md border bg-white p-1 shadow-lg">
                <button
                  className="flex w-full cursor-pointer items-center gap-2 rounded-md px-4 py-2 text-sm capitalize transition hover:bg-gray-50"
                  onClick={() => navigate("/profile?lang=" + savedLang)}
                >
                  {user?.first_name + " " + user?.last_name || "User"}
                </button>
                <button
                  className="flex w-full cursor-pointer items-center gap-2 rounded-md px-4 py-2 text-sm text-red-500 transition hover:bg-red-50 hover:text-red-500"
                  onClick={handleLogout}
                >
                  {t("logout")}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main content area */}
      <div className="relative flex flex-1 overflow-hidden">
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="absolute top-2 z-[999] hidden rounded-l-full border-2 border-r-0 border-purple-500 p-2 py-3 transition-all duration-200 hover:bg-purple-900 hover:text-white sm:block lg:top-8"
          >
            <FiMenu className="h-4 w-4 lg:h-6 lg:w-6" />
          </button>
        )}
        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 right-0 z-50 w-[83%] transform bg-[#FEFEFE] transition-transform duration-300 ease-in-out ${
            sidebarOpen ? "md:w-[25rem]" : "md:w-0"
          } xl:relative ${
            sidebarOpen ? "translate-x-0" : "translate-x-full xl:translate-x-full"
          } ltr:border-r rtl:border-l`}
        >
          {/* Force scrollbar to right */}
          <div dir="ltr" className="custom-scrollbar flex h-full flex-col overflow-y-auto">
            <div dir="rtl" className="flex flex-col">
              {renderSidebarContent()}
            </div>
          </div>
        </aside>

        {/* Overlay for mobile when sidebar is open */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 bg-black/40 xl:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Scrollable content */}
        <main ref={mainRef} className="custom-scrollbar flex flex-1 flex-col overflow-auto px-4 lg:px-16">
          {currentItem && (
            <>
              <h2 className="relative mb-4 mt-6 px-9 text-2xl font-medium tracking-tight sm:mb-10 sm:mt-14 sm:px-0 sm:text-3xl lg:mb-8 lg:mt-10">
                {!sidebarOpen && (
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className="absolute -right-5 -top-1 z-[999] rounded-l-full border-2 border-r-0 border-purple-500 p-2 py-3 transition-all duration-200 hover:bg-purple-900 hover:text-white sm:hidden lg:top-8"
                  >
                    <FiMenu className="h-4 w-4 lg:h-6 lg:w-6" />
                  </button>
                )}
                {currentItem?.name}
              </h2>
              {renderLessonContent()}
            </>
          )}
        </main>
      </div>

      {/* Hint Modal */}
      {hintModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="relative flex max-h-[80vh] w-full max-w-md flex-col rounded-xl bg-white p-6 shadow-xl">
            {/* Close Button */}
            <button
              onClick={() => setHintModal({ open: false, text: "" })}
              className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-md border text-gray-500 transition hover:text-gray-700"
            >
              <HiX className="h-5 w-5" />
            </button>

            {/* Image */}
            <img
              src="/HintIllustration.jpg"
              alt="Hint Illustration"
              className="mx-auto mb-4 h-40 w-auto sm:h-44"
            />

            <hr className="my-3" />

            {/* Scrollable Text */}
            <div
              className="prose max-h-[35vh] flex-1 overflow-y-auto px-2 text-right sm:max-h-[40vh] md:max-h-[45vh]"
              dangerouslySetInnerHTML={{
                __html: hintModal.text?.replace(/\r\n/g, "<br />"),
              }}
            />

            <hr className="my-3" />

            {/* Button */}
            <button
              onClick={() => setHintModal({ open: false, text: "" })}
              className="mt-2 rounded-lg bg-purple-500 px-6 py-2 font-medium text-white transition hover:bg-purple-600"
            >
              {t("tryAgain")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
